import { AuthRole, OrganizationRole, type Role } from '@/enums/roles';
import formTemplateRoutes from '@/modules/rie/router/formTemplate';
import actionPointRoutes from '@/router/actionPoints';
import adminRoutes from '@/router/admin';
import authRoutes from '@/router/auth';
import documentRoutes from '@/router/documents';
import employeeRoutes from '@/router/employees';
import moduleRoutes from '@/router/modules';
import organizationRoutes from '@/router/organizations';
import superAdminRoutes from '@/router/superAdmin';
import { useAuthStore } from '@/stores/auth';
import { useFilterStore } from '@/stores/components/filter';
import { useEmployeeStore } from '@/stores/employee';
import { useGlobalStore } from '@/stores/global';
import { useNotificationStore } from '@/stores/notification';
import type { Employee as EmployeeType } from '@/types/employee';
import toast from '@/utils/toast';
import Error from '@/views/errors/Error.vue';
import Manual from '@/views/Manual.vue';
import { createRouter, createWebHistory } from 'vue-router';

const router = createRouter({
    history: createWebHistory(),
    routes: [
        {
            path: '/',
            redirect: () => {
                const authStore = useAuthStore();
                if (authStore.isAuthenticated) return '/dashboard';
                return '/login';
            }
        },
        {
            path: '/home',
            redirect: '/'
        },
        ...actionPointRoutes,
        ...adminRoutes,
        ...authRoutes,
        ...moduleRoutes,
        ...formTemplateRoutes,
        ...documentRoutes,
        ...employeeRoutes,
        ...organizationRoutes,
        ...superAdminRoutes,
        {
            path: '/handleiding',
            name: 'manual',
            component: Manual,
            meta: {
                pageTitle: 'Handleiding',
                roles: [OrganizationRole.Employee]
            }
        },
        {
            name: 'error',
            path: '/error/:statusCode',
            component: Error
        },
        {
            path: '/:path(.*)*',
            name: 'notFound',
            component: Error
        }
    ],
    scrollBehavior() {
        return { top: 0 };
    }
});

router.beforeEach(async (to) => {
    if (to.meta.roles) {
        const authStore = useAuthStore();

        if (!authStore.isAuthenticated) {
            toast.error('LoginRequired');
            return { name: 'login' };
        }

        const notificationStore = useNotificationStore();
        await notificationStore.getForLoggedInEmployee();

        const allowedRoles = to.meta.roles;
        if (allowedRoles.includes(AuthRole.SuperAdmin) && authStore.isSuperAdmin()) return;
        if (allowedRoles.includes(AuthRole.SecurityOfficer) && authStore.isSecurityOfficer) return;

        if (!authStore.currentOrganization) {
            console.error('Er is geen laatst gebruikt bedrijf gevonden');
            return;
        }

        if (!authStore.isAuthorized(allowedRoles as OrganizationRole[], authStore.getCurrentOrganization())) {
            toast.error('AccessDenied');
            return { name: 'modules' };
        }
    }
});

router.beforeEach((to, from) => {
    if (to.meta.pageTitle) {
        const globalStore = useGlobalStore();
        globalStore.pageTitle = to.meta.pageTitle;
    }

    if (from.name === 'actionPoints' && to.name !== 'actionPoints') {
        const filterStore = useFilterStore();
        filterStore.selectedFilters = {};
    }
});

export async function redirectToStartPage(employee: EmployeeType | null = null): Promise<void> {
    const authStore = useAuthStore();
    const employeeStore = useEmployeeStore();

    if (employee == null) {
        await router.push('/login');
        return;
    }

    await authStore.updateEmployeeOrganizations();

    if (authStore.accessibleOrganizations?.length == 0) {
        toast.error('NoAccessibleOrganizations');
        authStore.logout();
        return;
    }

    if (authStore.loggedInEmployee?.currentOrganization) {
        await employeeStore.setCurrentOrganization(authStore.loggedInEmployee.currentOrganization);
    }

    if (authStore.currentOrganization == null) {
        await router.push('/bedrijf-selecteren');
        return;
    }

    if (authStore.isSuperAdmin()) {
        await router.push('/hoofdbeheer');
        return;
    }

    await router.push('/dashboard');
}

export default router;

declare module 'vue-router' {
    interface RouteMeta {
        pageTitle?: string;
        roles?: Role[];
    }
}
